import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  FormControl,
  FormLabel,
  Button,
  Spinner,
  Badge,
  Tooltip,
  Checkbox,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react'
import {
  Search,
  LayoutList,
  CheckCircle2,
  Mail,
  Ticket,
  Inbox,
  Clock,
  BarChart2,
  CheckCheck,
  Timer,
  Trash2,
} from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import type { MetaData } from '@/components/tables/types'
import {
  TimerCell,
  QueueWaitCell,
  ComposeEmailDrawer,
  TicketRequestTriageDrawer,
  AliceCallOutConfirmModal,
  RequestSourceBadge,
  type VoiceAiCallPayload,
} from '@/components/helpdesk'
import { buildVoiceAiCallPayload } from '@/lib/voiceAi/buildVoiceAiCallPayload'
import { ALICE_CALL_OUT_ENABLED } from '@/lib/vicidial'
import { apiClient } from '@/lib/api/client'
import { acknowledgeTicketRequest } from '@/lib/services/ticketRequestAcknowledge'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAuthStore } from '@/store/auth'
import { AgentAssignMenu } from '@/components/helpdesk/AgentAssignMenu'
import { useAssignAgentList } from '@/hooks/useAssignAgentList'
import { canAssignSupportAgent } from '@/lib/utils/roleHelper'
import { assignAgentDisplayName } from '@/lib/utils/assignAgents'

/** Format date as "30 Apr 2025 - 09:30 AM" */
function formatSubmittedAt(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  const minStr = minutes < 10 ? `0${minutes}` : String(minutes)
  return `${day} ${month} ${year} - ${h12}:${minStr} ${ampm}`
}

// Base column config; filter options are merged from API in component.
const INCOMING_REQUEST_FIELDS: DataTableOptions['dataFields'] = {
  request_number: {
    name: 'Request ID',
    withSort: true,
    fullWidth: true,
  },
  client_id: {
    name: 'Client',
    withSort: true,
    withFilter: true,
    filterOptions: [],
    maxWidth: '160px',
  },
  service_type: {
    name: 'Request Type',
    withSort: true,
    withFilter: true,
    filterOptions: [],
    maxWidth: '130px',
  },
  request_source: {
    name: 'Source',
    withSort: true,
    maxWidth: '140px',
  },
  email_subject: {
    name: 'Subject',
    withSort: false,
    maxWidth: '280px',
  },
  submitted_at: {
    name: 'Received Date',
    withSort: true,
    maxWidth: '200px',
  },
  timer: {
    name: 'Timer',
    withSort: false,
    noTruncate: true,
    maxWidth: '180px',
  },
}

/** Assign to column — team leads get a dropdown; others see assigned user name only */
const ASSIGN_TO_FIELD: DataTableOptions['dataFields'] = {
  assign_to: {
    name: 'Assigned to',
    withSort: false,
    maxWidth: '220px',
  },
}

/** Acknowledged tab aging columns */
const ACKNOWLEDGED_FIELDS: DataTableOptions['dataFields'] = {
  acknowledged_at: {
    name: 'Acknowledged At',
    withSort: true,
    maxWidth: '200px',
  },
  ack_by: {
    name: 'Acknowledged By',
    withSort: false,
    maxWidth: '160px',
  },
  ack_delay: {
    name: 'Ack SLA',
    withSort: false,
    maxWidth: '120px',
  },
  queue_wait: {
    name: 'Queue Wait',
    withSort: false,
    noTruncate: true,
    maxWidth: '140px',
  },
}

type FilterOption = { value: string | number; label: string }

type TabValue = 'all' | 'acknowledge' | 'trash'

const TAB_INDEX: Record<TabValue, number> = { all: 0, acknowledge: 1, trash: 2 }

function tabFromIndex(index: number): TabValue {
  if (index === 1) return 'acknowledge'
  if (index === 2) return 'trash'
  return 'all'
}

type PendingTrashAction = { kind: 'soft' | 'force'; id: number }

type PendingAcknowledgeAction = { row: Record<string, unknown> }

function trashConfirmMessage(action: PendingTrashAction): string {
  switch (action.kind) {
    case 'soft':
      return 'Move this request to trash? You can restore it later from the Trash tab.'
    case 'force':
      return 'Permanently delete this request? This cannot be undone.'
  }
}

interface StatusBadgeProps {
  label: string
  count: number
  colorScheme: 'purple' | 'blue' | 'teal' | 'orange'
}

function StatusBadge({ label, count, colorScheme }: StatusBadgeProps) {
  const colorMap = {
    purple: { bg: 'purple.50', color: 'purple.700', border: 'purple.200' },
    blue: { bg: 'blue.50', color: 'blue.700', border: 'blue.200' },
    teal: { bg: 'teal.50', color: 'teal.700', border: 'teal.200' },
    orange: { bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
  }
  const colors = colorMap[colorScheme]

  return (
    <Badge
      borderRadius="md"
      px={3}
      py={1.5}
      fontWeight="600"
      fontSize="sm"
      bg={colors.bg}
      color={colors.color}
      borderWidth="1px"
      borderColor={colors.border}
      variant="outline"
    >
      {label} : {count}
    </Badge>
  )
}

/** Incoming dashboard stats from GET /support/incoming/stats */
interface IncomingStats {
  total_open: number
  total_pending: number
  total_requests: number
  total_available_agents: number
  acknowledgement_rate: number
  on_time_ack_rate: number
  avg_ack_time_minutes: number
  volume_per_client: Array<{ client_name: string; count: number }>
  volume_per_transaction_type: Array<{ name: string; count: number }>
  volume_per_source: Array<{ source: string; count: number }>
}

function StatCard({
  label,
  value,
  colorScheme,
  icon,
}: {
  label: string
  value: string | number
  colorScheme: 'blue' | 'orange' | 'teal' | 'purple' | 'gray'
  icon?: React.ReactNode
}) {
  const colorMap = {
    blue: { bg: 'blue.50', color: 'blue.700', border: 'blue.200' },
    orange: { bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
    teal: { bg: 'teal.50', color: 'teal.700', border: 'teal.200' },
    purple: { bg: 'purple.50', color: 'purple.700', border: 'purple.200' },
    gray: { bg: 'gray.50', color: 'gray.700', border: 'gray.200' },
  }
  const c = colorMap[colorScheme]
  return (
    <Card size="sm" borderWidth="1px" borderColor={c.border} bg={c.bg}>
      <CardBody py={3} px={4}>
        <HStack justify="space-between" align="flex-start">
          <Text fontSize="xs" fontWeight="600" color={c.color} textTransform="uppercase" letterSpacing="wider">
            {label}
          </Text>
          {icon && <Box color={c.color} opacity={0.7}>{icon}</Box>}
        </HStack>
        <Text fontSize="2xl" fontWeight="700" color={c.color} mt={1}>
          {value}
        </Text>
      </CardBody>
    </Card>
  )
}

const MEMBERS_API_PATH = '/user-management/users/members'
const INCOMING_REQUEST_MARKER_POLL_MS = 30_000

interface AvailableAgentRow {
  id?: number
  first_name?: string
  last_name?: string
  user_login?: string
  assigned_tickets_count?: number
  active_tickets_count?: number
}

export default function IncomingRequestPage() {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const user = useAuthStore((s) => s.user)
  const canAssignAgent = canAssignSupportAgent(user)
  const {
    agents: assignPoolAgents,
    reload: reloadAssignAgents,
    path: assignAgentsPath,
    assignPool,
  } = useAssignAgentList(canAssignAgent)
  const access = useAccessCheck()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const latestPendingRequestIdRef = useRef<number | null>(null)
  const latestPendingMarkerInitializedRef = useRef(false)
  const isCheckingPendingMarkerRef = useRef(false)

  const {
    isOpen: isTrashConfirmOpen,
    onOpen: onTrashConfirmOpen,
    onClose: onTrashConfirmCloseBase,
  } = useDisclosure()
  const {
    isOpen: isAckConfirmOpen,
    onOpen: onAckConfirmOpen,
    onClose: onAckConfirmCloseBase,
  } = useDisclosure()
  const [pendingTrashAction, setPendingTrashAction] = useState<PendingTrashAction | null>(null)
  const [pendingAckAction, setPendingAckAction] = useState<PendingAcknowledgeAction | null>(null)
  const [ackCreateTicket, setAckCreateTicket] = useState(true)
  const [isTrashConfirmProcessing, setIsTrashConfirmProcessing] = useState(false)
  const [isAckConfirmProcessing, setIsAckConfirmProcessing] = useState(false)

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [isTabSwitching, setIsTabSwitching] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [totalAcknowledged, setTotalAcknowledged] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)

  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [clientOptions, setClientOptions] = useState<FilterOption[]>([])
  const [requestTypeOptions, setRequestTypeOptions] = useState<FilterOption[]>([])
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null)
  const [stats, setStats] = useState<IncomingStats | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [availableAgents, setAvailableAgents] = useState<AvailableAgentRow[]>([])
  const [isLoadingAgents, setIsLoadingAgents] = useState(false)
  const [composeRow, setComposeRow] = useState<Record<string, unknown> | null>(null)
  const [viewRow, setViewRow] = useState<Record<string, unknown> | null>(null)
  const [callOutPayload, setCallOutPayload] = useState<VoiceAiCallPayload | null>(null)
  const [assignMemberList, setAssignMemberList] = useState<AvailableAgentRow[]>([])
  const resolvedAssignMembers = canAssignAgent ? assignPoolAgents : assignMemberList
  const [assigningId, setAssigningId] = useState<number | null>(null)
  const { isOpen: isCallOutOpen, onOpen: onCallOutOpen, onClose: onCallOutClose } = useDisclosure()

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clientsRes, typesRes] = await Promise.all([
          apiClient.get('/options/clients'),
          apiClient.get('/options/service-types/request-types'),
        ])
        const clients = Array.isArray(clientsRes.data) ? clientsRes.data : []
        const types = Array.isArray(typesRes.data) ? typesRes.data : []
        setClientOptions(
          clients.map((c: { id: number; name?: string; code?: string }) => ({
            value: c.id,
            label: c.name || c.code || String(c.id),
          }))
        )
        setRequestTypeOptions(
          types.map((t: { id: number; name?: string; code?: string }) => ({
            value: t.id,
            label: t.name || t.code || String(t.id),
          }))
        )
      } catch {
        setClientOptions([])
        setRequestTypeOptions([])
      }
    }
    loadOptions()
  }, [])

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoadingStats(true)
      try {
        // Stats should always reflect all incoming requests (no date filtering)
        const res = await apiClient.get('/support/incoming/stats')
        setStats(res.data ?? null)
      } catch {
        setStats(null)
      } finally {
        setIsLoadingStats(false)
      }
    }
    fetchStats()
  }, [])

  useEffect(() => {
    if (!canAssignAgent) return
    const fetchAgents = async () => {
      setIsLoadingAgents(true)
      try {
        const res = await apiClient.get(assignAgentsPath, {
          params: {
            per_page: 10,
            order: 'assigned_tickets_count',
            sort: 'asc',
            ...(assignPool ? { assign_pool: 1 } : {}),
          },
        })
        const data = res.data?.data ?? res.data ?? []
        setAvailableAgents(Array.isArray(data) ? data : [])
      } catch {
        setAvailableAgents([])
      } finally {
        setIsLoadingAgents(false)
      }
    }
    fetchAgents()
  }, [canAssignAgent, assignAgentsPath, assignPool])

  // Name lookup for users who cannot assign. Assigners use the full assign pool.
  useEffect(() => {
    if (canAssignAgent) return
    const fetchMembers = async () => {
      try {
        const res = await apiClient.get(MEMBERS_API_PATH, {
          params: { per_page: 100, order: 'first_name', sort: 'asc' },
        })
        const data = res.data?.data ?? res.data ?? []
        setAssignMemberList(Array.isArray(data) ? data : [])
      } catch {
        setAssignMemberList([])
      }
    }
    fetchMembers()
  }, [canAssignAgent, assignAgentsPath])

  const refreshIncomingStats = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/incoming/stats')
      setStats(res.data ?? null)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    const checkLatestPendingRequestMarker = async () => {
      if (document.visibilityState !== 'visible' || isCheckingPendingMarkerRef.current) {
        return
      }

      isCheckingPendingMarkerRef.current = true
      try {
        const res = await apiClient.get('/support/incoming/latest-pending')
        const latestId = res.data?.id != null ? Number(res.data.id) : null
        const normalizedLatestId = Number.isFinite(latestId) ? latestId : null

        if (!latestPendingMarkerInitializedRef.current) {
          latestPendingRequestIdRef.current = normalizedLatestId
          latestPendingMarkerInitializedRef.current = true
          return
        }

        const previousId = latestPendingRequestIdRef.current
        if (normalizedLatestId !== null && (previousId === null || normalizedLatestId > previousId)) {
          latestPendingRequestIdRef.current = normalizedLatestId
          tableRef.current?.reload()
          void refreshIncomingStats()
        }
      } catch {
        // Marker polling is opportunistic; the normal table fetch still handles user-visible errors.
      } finally {
        isCheckingPendingMarkerRef.current = false
      }
    }

    void checkLatestPendingRequestMarker()
    const intervalId = window.setInterval(() => void checkLatestPendingRequestMarker(), INCOMING_REQUEST_MARKER_POLL_MS)

    return () => window.clearInterval(intervalId)
  }, [refreshIncomingStats])

  const finishAcknowledge = useCallback(
    (createTicket: boolean, parentTicketId?: number) => {
      tableRef.current?.reload()
      void refreshIncomingStats()

      if (createTicket && parentTicketId) {
        navigate(`/tickets/${parentTicketId}/edit`)
        return
      }

      setActiveTab('acknowledge')
    },
    [navigate, refreshIncomingStats],
  )

  const handleAcknowledge = useCallback(
    async (row: Record<string, unknown>, createTicket = true) => {
      const id = row.id as number
      if (!id) return

      setAcknowledgingId(id)
      try {
        const { ticketRequest: updatedReq, parentTicket: parent, warnings } = await acknowledgeTicketRequest(
          id,
          createTicket,
        )
        const ticketNumber =
          (parent?.ticket_number as string | undefined) ??
          (parent?.ticketNumber as string | undefined)

        if (warnings.length > 0) {
          showSuccess(
            'Request acknowledged with warnings',
            [
              createTicket && ticketNumber
                ? `Ticket ${ticketNumber} created.`
                : 'Request acknowledged.',
              ...warnings,
            ].join(' '),
          )
        } else {
          showSuccess(
            'Request acknowledged',
            createTicket && ticketNumber
              ? `Ticket ${ticketNumber} created.`
              : 'Create a ticket from the Acknowledged tab when ready.',
          )
        }

        setViewRow((prev) => {
          if (!prev || (prev.id as number) !== id) return prev
          const merged: Record<string, unknown> = updatedReq
            ? { ...prev, ...updatedReq }
            : { ...prev, acknowledged_at: new Date().toISOString() }
          if (parent) {
            merged.parent_ticket_id = parent.id
            merged.parent_ticket_number = parent.ticket_number ?? parent.ticketNumber
          }
          return merged
        })

        finishAcknowledge(createTicket, parent?.id as number | undefined)
        if (canAssignAgent) {
          try {
            const agentsRes = await apiClient.get(assignAgentsPath, {
              params: {
                per_page: 10,
                order: 'assigned_tickets_count',
                sort: 'asc',
                ...(assignPool ? { assign_pool: 1 } : {}),
              },
            })
            const data = agentsRes.data?.data ?? agentsRes.data ?? []
            setAvailableAgents(Array.isArray(data) ? data : [])
          } catch {
            // ignore agents refresh error
          }
        }
      } catch (error: unknown) {
        const ax = error as {
          response?: { status?: number; data?: { message?: string } }
          message?: string
        }
        const message = String(ax.response?.data?.message ?? ax.message ?? '')
        const alreadyAcknowledged =
          ax.response?.status === 400 &&
          /already been acknowledged/i.test(message)

        if (alreadyAcknowledged) {
          showSuccess('Already acknowledged', 'This request was already acknowledged. Refreshing the list.')
          tableRef.current?.reload()
          void refreshIncomingStats()
          setViewRow((prev) => {
            if (!prev || (prev.id as number) !== id) return prev
            return { ...prev, acknowledged_at: prev.acknowledged_at ?? new Date().toISOString() }
          })
          return
        }

        showError(error)
      } finally {
        setAcknowledgingId(null)
      }
    },
    [showSuccess, showError, canAssignAgent, assignAgentsPath, assignPool, finishAcknowledge, refreshIncomingStats],
  )

  const handleCreateTicket = useCallback(
    (row: Record<string, unknown>) => {
      navigate('/tickets/create', {
        state: { fromRequest: row },
      })
    },
    [navigate]
  )

  const handleAssign = useCallback(
    async (row: Record<string, unknown>, userId: number | null) => {
      const id = row.id as number
      if (!id) return
      setAssigningId(id)
      try {
        await apiClient.patch(`/support/my-request/${id}/assign`, { assigned_to: userId })
        showSuccess('Success', userId ? 'Request assigned successfully' : 'Request unassigned')
        tableRef.current?.reload()
        try {
          const res = await apiClient.get('/support/incoming/stats')
          setStats(res.data ?? null)
        } catch {
          // ignore
        }
        if (canAssignAgent) {
          try {
            const agentsRes = await apiClient.get(assignAgentsPath, {
              params: {
                per_page: 10,
                order: 'assigned_tickets_count',
                sort: 'asc',
                ...(assignPool ? { assign_pool: 1 } : {}),
              },
            })
            const data = agentsRes.data?.data ?? agentsRes.data ?? []
            setAvailableAgents(Array.isArray(data) ? data : [])
            void reloadAssignAgents()
          } catch {
            // ignore
          }
        }
      } catch (error: any) {
        showError(error)
      } finally {
        setAssigningId(null)
      }
    },
    [showSuccess, showError, canAssignAgent, assignAgentsPath, assignPool, reloadAssignAgents]
  )

  const onTrashConfirmClose = useCallback(() => {
    setPendingTrashAction(null)
    onTrashConfirmCloseBase()
  }, [onTrashConfirmCloseBase])

  const onAckConfirmClose = useCallback(() => {
    setPendingAckAction(null)
    setAckCreateTicket(true)
    onAckConfirmCloseBase()
  }, [onAckConfirmCloseBase])

  const openAckConfirm = useCallback(
    (row: Record<string, unknown>) => {
      setPendingAckAction({ row })
      setAckCreateTicket(true)
      onAckConfirmOpen()
    },
    [onAckConfirmOpen],
  )

  const handleConfirmAcknowledge = useCallback(async () => {
    if (!pendingAckAction?.row) return
    setIsAckConfirmProcessing(true)
    try {
      await handleAcknowledge(pendingAckAction.row, ackCreateTicket)
      onAckConfirmClose()
    } finally {
      setIsAckConfirmProcessing(false)
    }
  }, [pendingAckAction, ackCreateTicket, handleAcknowledge, onAckConfirmClose])

  const openTrashConfirm = useCallback(
    (action: PendingTrashAction) => {
      setPendingTrashAction(action)
      onTrashConfirmOpen()
    },
    [onTrashConfirmOpen]
  )

  const handleTabChange = useCallback((index: number) => {
    setIsTabSwitching(true)
    setActiveTab(tabFromIndex(index))
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearData()
    tableRef.current?.clearPage()
  }, [])

  const handleDataLoad = useCallback((_data: unknown[], meta?: MetaData) => {
    setIsTabSwitching(false)
    if (meta?.pending != null) setTotalRows(meta.pending)
    if (meta?.acknowledged != null) setTotalAcknowledged(meta.acknowledged)
    if (meta?.trashed != null) setTotalTrash(meta.trashed)
  }, [])

  const handleConfirmTrashAction = useCallback(async () => {
    if (!pendingTrashAction) return
    const { id, kind } = pendingTrashAction
    setIsTrashConfirmProcessing(true)
    try {
      if (kind === 'force') {
        await apiClient.delete(`/support/my-request/archived/${id}`)
        showSuccess('Success', 'Request permanently deleted')
      } else {
        await apiClient.delete(`/support/my-request/${id}`)
        showSuccess('Success', 'Request moved to trash')
      }
      setViewRow(null)
      tableRef.current?.reload()
      await refreshIncomingStats()
      onTrashConfirmClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsTrashConfirmProcessing(false)
    }
  }, [pendingTrashAction, showSuccess, showError, onTrashConfirmClose, refreshIncomingStats])

  const handleOpenCallOut = useCallback(
    (row: Record<string, unknown>) => {
      const payload = buildVoiceAiCallPayload(row)
      const defaultClientCode = String((user as { default_client_code?: string })?.default_client_code ?? '').trim()
      if (!payload.ticket.metadata.clientCode && defaultClientCode) {
        payload.ticket.metadata.clientCode = defaultClientCode
      }
      setCallOutPayload(payload)
      onCallOutOpen()
    },
    [onCallOutOpen, user]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      useRowContextMenu: true,
      dataFields: {
        request_number: INCOMING_REQUEST_FIELDS.request_number,
        client_id: {
          ...INCOMING_REQUEST_FIELDS.client_id,
          filterOptions: clientOptions,
        },
        service_type: {
          ...INCOMING_REQUEST_FIELDS.service_type,
          filterOptions: requestTypeOptions,
        },
        request_source: INCOMING_REQUEST_FIELDS.request_source,
        email_subject: INCOMING_REQUEST_FIELDS.email_subject,
        submitted_at: INCOMING_REQUEST_FIELDS.submitted_at,
        ...(activeTab !== 'acknowledge' ? ASSIGN_TO_FIELD : {}),
        ...(activeTab === 'all' ? { timer: INCOMING_REQUEST_FIELDS.timer } : {}),
        ...(activeTab === 'acknowledge' ? ACKNOWLEDGED_FIELDS : {}),
      },
      dataSource: activeTab === 'trash' ? '/support/my-request/archived' : '/support/my-request',
      primaryKey: 'id',
      softDelete: activeTab === 'trash',
      displayInModal: false,
      noRecordText: 'No Record Found',
      hideSelection: true,
      otherActions: [
        {
          name: 'Acknowledge',
          onClick: openAckConfirm,
          show: (row: Record<string, unknown>) => !row.acknowledged_at,
          render: (row: Record<string, unknown>, _isLoading?: boolean, onClick?: () => void) => {
            const isAcknowledging = acknowledgingId === (row.id as number)
            return (
              <Tooltip label="Save & Acknowledge" placement="top" hasArrow>
                <IconButton
                  aria-label="Save and acknowledge"
                  icon={<CheckCircle2 size={18} />}
                  variant="solid"
                  size="sm"
                  colorScheme="blue"
                  isLoading={isAcknowledging}
                  isDisabled={isAcknowledging}
                  onClick={(e) => {
                    e.stopPropagation()
                    onClick?.()
                  }}
                />
              </Tooltip>
            )
          },
        },
        {
          name: 'Create Ticket',
          onClick: handleCreateTicket,
          show: (row: Record<string, unknown>) => !!row.acknowledged_at && !row.parent_ticket_id,
          render: (_row: Record<string, unknown>) => (
            <Tooltip label="Create Ticket" placement="top" hasArrow>
              <IconButton
                aria-label="Create Ticket"
                icon={<Ticket size={18} />}
                variant="solid"
                size="sm"
                colorScheme="green"
                onClick={(e) => {
                  e.stopPropagation()
                  handleCreateTicket(_row)
                }}
              />
            </Tooltip>
          ),
        },
        {
          name: 'Compose Email',
          onClick: (row: Record<string, unknown>) => {
            setComposeRow(row)
          },
          show: () => true,
          render: (_row: Record<string, unknown>) => (
            <Tooltip label="Compose Email" placement="top" hasArrow>
              <IconButton
                aria-label="Compose Email"
                icon={<Mail size={18} />}
                variant="ghost"
                size="sm"
                colorScheme="gray"
                onClick={(e) => {
                  e.stopPropagation()
                  setComposeRow(_row)
                }}
              />
            </Tooltip>
          ),
        },
      ],
    }),
    [
      activeTab,
      clientOptions,
      requestTypeOptions,
      handleAcknowledge,
      openAckConfirm,
      handleCreateTicket,
      acknowledgingId,
      assigningId,
    ]
  )

  const permissions = useMemo(
    () => ({
      can_edit: false,
      can_delete: access.can_delete,
      can_create: false,
      can_view: true,
      can_export: false,
      can_import: false,
    }),
    [access.can_delete]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      incoming: 1,
      ...(activeTab === 'all' && { acknowledged: 0 }),
      ...(activeTab === 'acknowledge' && { acknowledged: 1 }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    }),
    [activeTab, searchQuery, dateFrom, dateTo]
  )

  const customRenderers = useMemo(
    () => ({
      client_id: (_value: unknown, row?: Record<string, unknown>) => {
        const name = (row?.client_name ?? row?.contact_name) as string | null | undefined
        return <Text as="span">{name ?? '–'}</Text>
      },
      request_number: (value: unknown) => {
        const str = value ? String(value) : '–'
        return <Text as="span" whiteSpace="nowrap">{str}</Text>
      },
      request_source: (value: string | null | undefined, row?: Record<string, unknown>) => {
        const source = (value ?? row?.request_source) as string | null | undefined
        if (!source) return <Text as="span" color="gray.500">–</Text>
        const label = row?.source_label as string | null | undefined
        return <RequestSourceBadge source={source} label={label} />
      },
      email_subject: (value: unknown, row?: Record<string, unknown>) => {
        const subject = (value ?? row?.description) as string | null | undefined
        const text = subject || '–'
        if (!row) {
          return <Text as="span">{text}</Text>
        }
        return (
          <Text
            as="button"
            type="button"
            variant="link"
            color={colors.brandBlue}
            fontWeight="500"
            textAlign="left"
            textDecoration="underline"
            cursor="pointer"
            whiteSpace="normal"
            noOfLines={3}
            title={text !== '–' ? text : undefined}
            onClick={(e) => {
              e.stopPropagation()
              setViewRow(row)
            }}
          >
            {text}
          </Text>
        )
      },
      submitted_at: (value: string | null | undefined) => formatSubmittedAt(value),
      acknowledged_at: (value: string | null | undefined) => formatSubmittedAt(value),
      ack_by: (_value: unknown, row?: Record<string, unknown>) => {
        const name = (row?.acknowledged_by_name as string | null | undefined)?.trim()
        return (
          <Text as="span" fontSize="sm" noOfLines={2} title={name ?? undefined} color={name ? 'inherit' : 'gray.500'}>
            {name || '–'}
          </Text>
        )
      },
      ack_delay: (_value: unknown, row?: Record<string, unknown>) => {
        const delayMinutes = (row?.acknowledge_delay_minutes as number | null | undefined) ?? 0
        if (delayMinutes > 0) {
          return <Badge colorScheme="orange">{delayMinutes} min late</Badge>
        }
        return <Badge colorScheme="green">On time</Badge>
      },
      queue_wait: (_value: unknown, row?: Record<string, unknown>) => (
        <QueueWaitCell
          acknowledgedAt={row?.acknowledged_at as string | null | undefined}
          parentTicketCreatedAt={row?.parent_ticket_created_at as string | null | undefined}
        />
      ),
      timer: (value: unknown, row?: Record<string, unknown>) => {
        const submittedAt = (row?.submitted_at ?? value) as string | null | undefined
        if (!submittedAt || typeof submittedAt !== 'string') return <Text as="span" color="gray.500">–</Text>
        const acknowledgedAt = row?.acknowledged_at as string | null | undefined
        const delayMinutes = (row?.acknowledge_delay_minutes as number | null | undefined) ?? 0
        if (acknowledgedAt) {
          const label = delayMinutes > 0
            ? `Acknowledged at ${formatSubmittedAt(acknowledgedAt)} (${delayMinutes} min late)`
            : `Acknowledged at ${formatSubmittedAt(acknowledgedAt)} (on time)`
          return (
            <Text as="span" color={delayMinutes > 0 ? 'orange.600' : 'green.600'} title={label}>
              {label}
            </Text>
          )
        }
        return <TimerCell requestedAt={submittedAt} acknowledgeDueAt={row?.acknowledge_due_at as string | null | undefined} />
      },
      assign_to: (_value: unknown, row?: Record<string, unknown>) => {
        if (!row || row.parent_ticket_id) return <Text as="span" color="gray.500">–</Text>
        const assignedToId = row.assigned_to as number | null | undefined
        const assignedToName = row.assigned_to_name as string | null | undefined
        const member = resolvedAssignMembers.find((m) => m.id === assignedToId)
        const assignedName =
          (assignedToName && assignedToName.trim()) ||
          (member
            ? assignAgentDisplayName(member)
            : assignedToId != null
              ? `User #${assignedToId}`
              : null)
        const isAssigning = assigningId === (row.id as number)

        if (!canAssignAgent) {
          return (
            <Text fontSize="sm" noOfLines={2} title={assignedName ?? undefined} color={assignedName ? 'inherit' : 'gray.500'}>
              {assignedName ?? '–'}
            </Text>
          )
        }

        return (
          <HStack w="full" justify="space-between" spacing={2} onClick={(e) => e.stopPropagation()}>
            {assignedName ? (
              <Text fontSize="sm" noOfLines={1} title={assignedName} flex="1" minW={0} textAlign="left">
                {assignedName}
              </Text>
            ) : (
              <Box flex="1" minW={0} />
            )}
            <AgentAssignMenu
              agents={resolvedAssignMembers}
              assignedToId={assignedToId}
              isAssigning={isAssigning}
              onAssign={(userId) => handleAssign(row, userId)}
            />
          </HStack>
        )
      },
    }),
    [resolvedAssignMembers, handleAssign, assigningId, canAssignAgent, colors.brandBlue]
  )

  const handleSearch = () => {
    const query = searchRef.current?.value ?? ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Box p={6} w="100%" minW={0} display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch">
        <Card display="flex" flexDirection="column">


          <CardBody
            p={4}
            display="flex"
            flexDirection="column"
            position="relative"
          >
            <Box
              display="flex"
              flexDirection={{ base: 'column', lg: 'row' }}
              gap={4}
              alignItems="stretch"
            >
              {/* Left column – Stats & volume analytics */}
              <Box
                flex="1"
                minH={0}
                display="flex"
                flexDirection="column"
                gap={3}
              >
                {/* Row 1 – Operational Cards */}
                <Box flexShrink={0}>
                  {isLoadingStats ? (
                    <HStack spacing={3}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.500">
                        Loading stats…
                      </Text>
                    </HStack>
                  ) : stats ? (
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                      <StatCard label="Total Open" value={stats.total_open} colorScheme="blue" icon={<Inbox size={16} />} />
                      <StatCard label="Total Pending" value={stats.total_pending} colorScheme="orange" icon={<Clock size={16} />} />
                      <StatCard label="Total Requests" value={stats.total_requests ?? 0} colorScheme="teal" icon={<BarChart2 size={16} />} />
                    </SimpleGrid>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Unable to load stats
                    </Text>
                  )}
                </Box>

                {/* Row 2 – SLA KPI Cards */}
                {stats && (
                  <Box flexShrink={0}>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                      <StatCard
                        label="Acknowledgement Rate"
                        value={`${stats.acknowledgement_rate}%`}
                        colorScheme="purple"
                        icon={<CheckCheck size={16} />}
                      />
                      <StatCard
                        label="On-Time Ack Rate"
                        value={`${stats.on_time_ack_rate}%`}
                        colorScheme="purple"
                        icon={<CheckCircle2 size={16} />}
                      />
                      <StatCard
                        label="Avg Ack Time"
                        value={`${stats.avg_ack_time_minutes} min`}
                        colorScheme="gray"
                        icon={<Timer size={16} />}
                      />
                    </SimpleGrid>
                  </Box>
                )}

                {/* Row 3 – Volume Analytics */}
                {stats && (
                  <Box flexShrink={0}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      <Card size="sm" borderWidth="1px" borderColor="gray.200">
                        <CardHeader py={2} px={4}>
                          <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                            Per Client
                          </Heading>
                        </CardHeader>
                        <CardBody pt={0} px={4} pb={3}>
                          <Box maxH="280px" overflowY="auto" pr={2}>
                            <VStack align="stretch" spacing={1}>
                              {stats.volume_per_client.length === 0 ? (
                                <Text fontSize="sm" color="gray.500">
                                  No data
                                </Text>
                              ) : (
                                stats.volume_per_client.map(({ client_name, count }) => (
                                  <HStack key={client_name} justify="space-between" flexShrink={0}>
                                    <Text fontSize="sm" noOfLines={1}>
                                      {client_name}
                                    </Text>
                                    <Text fontSize="sm" fontWeight="600">
                                      {count}
                                    </Text>
                                  </HStack>
                                ))
                              )}
                            </VStack>
                          </Box>
                        </CardBody>
                      </Card>
                      <Card size="sm" borderWidth="1px" borderColor="gray.200">
                        <CardHeader py={2} px={4}>
                          <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                            Per Transaction Type
                          </Heading>
                        </CardHeader>
                        <CardBody pt={0} px={4} pb={3}>
                          <VStack align="stretch" spacing={1}>
                            {stats.volume_per_transaction_type.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No data
                              </Text>
                            ) : (
                              stats.volume_per_transaction_type.map(({ name, count }) => (
                                <HStack key={name} justify="space-between">
                                  <Text fontSize="sm" noOfLines={1}>
                                    {name}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="600">
                                    {count}
                                  </Text>
                                </HStack>
                              ))
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                      <Card size="sm" borderWidth="1px" borderColor="gray.200">
                        <CardHeader py={2} px={4}>
                          <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                            Per Source
                          </Heading>
                        </CardHeader>
                        <CardBody pt={0} px={4} pb={3}>
                          <VStack align="stretch" spacing={1}>
                            {stats.volume_per_source.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No data
                              </Text>
                            ) : (
                              stats.volume_per_source.map(({ source, count }) => (
                                <HStack key={source} justify="space-between">
                                  <Text fontSize="sm" textTransform="capitalize">
                                    {source}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="600">
                                    {count}
                                  </Text>
                                </HStack>
                              ))
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                    </SimpleGrid>
                  </Box>
                )}
              </Box>

              {/* Right column – Available Agents (TL/STL only) */}
              {canAssignAgent && (
                <Box flex={{ base: '0 0 auto', lg: '0 0 320px' }} maxW={{ base: '100%', lg: '340px' }}>
                  <Card size="sm" borderWidth="1px" borderColor="gray.200" h="100%">
                    <CardHeader py={2} px={4}>
                      <HStack justify="space-between" align="center">
                        <Box>
                          <Heading size="sm">Available Agents</Heading>
                          <Text fontSize="xs" color="gray.500">
                            10 agents with fewest assigned tickets
                          </Text>
                        </Box>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => navigate('/agent-status')}
                        >
                          View All
                        </Button>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0} px={4} pb={4} display="flex" flexDirection="column" minH={0}>
                      {isLoadingAgents ? (
                        <HStack spacing={2} py={4}>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.500">Loading agents…</Text>
                        </HStack>
                      ) : availableAgents.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" py={4}>No agents found.</Text>
                      ) : (
                        <VStack align="stretch" spacing={2}>
                          <SimpleGrid columns={3} spacing={2} fontSize="xs" color="gray.500" pb={1} borderBottom="1px" borderColor="gray.200" w="100%" templateColumns="1fr auto auto">
                            <Text fontWeight="600">Name</Text>
                            <Text fontWeight="600" textAlign="center" pl={4}>Total</Text>
                            <Text fontWeight="600" textAlign="center" pl={2}>Active</Text>
                          </SimpleGrid>
                          {availableAgents.map((agent, index) => {
                            const name = [agent.first_name, agent.last_name].filter(Boolean).join(' ') || agent.user_login || '–'
                            const total = agent.assigned_tickets_count ?? 0
                            const active = agent.active_tickets_count ?? 0
                            return (
                              <SimpleGrid key={agent.id ?? index} columns={3} spacing={2} w="100%" alignItems="center" templateColumns="1fr auto auto">
                                <Tooltip label={name} placement="top" hasArrow>
                                  <Text fontSize="sm" noOfLines={2} title={name}>{name}</Text>
                                </Tooltip>
                                <Box pl={4}>
                                  <Text fontSize="sm" textAlign="center"><Badge size="sm" colorScheme="blue" variant="subtle">{total}</Badge></Text>
                                </Box>
                                <Box pl={2}>
                                  <Text fontSize="sm" textAlign="center"><Badge size="sm" colorScheme="teal" variant="subtle">{active}</Badge></Text>
                                </Box>
                              </SimpleGrid>
                            )
                          })}
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                </Box>
              )}
            </Box>

            <Box flexShrink={0} mt={6} mb={4}>
              <Tabs index={TAB_INDEX[activeTab]} onChange={handleTabChange} variant="enclosed">
                <TabList borderBottom="none">
                  <Tab>
                    <HStack spacing={2}>
                      <LayoutList size={14} />
                      <Text>All Request</Text>
                      <Badge colorScheme="blue">{totalRows}</Badge>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack spacing={2}>
                      <CheckCircle2 size={14} />
                      <Text>Acknowledge</Text>
                      <Badge colorScheme="green">{totalAcknowledged}</Badge>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack spacing={2}>
                      <Trash2 size={14} />
                      <Text>Trash</Text>
                      <Badge colorScheme="red">{totalTrash}</Badge>
                    </HStack>
                  </Tab>
                </TabList>
              </Tabs>
              <Box borderBottom="2px" borderColor={colors.border} />
            </Box>
            <Box flexShrink={0} mb={4}>
              <HStack justify="flex-start" align="flex-end" spacing={4} width="100%" flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request #, contact, description..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <HStack align="flex-end" spacing={2}>
                  <FormControl width="auto">
                    <FormLabel fontSize="xs" mb={1} whiteSpace="nowrap">
                      From
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      max={dateTo || undefined}
                    />
                  </FormControl>
                  <FormControl width="auto">
                    <FormLabel fontSize="xs" mb={1} whiteSpace="nowrap">
                      To
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      min={dateFrom || undefined}
                    />
                  </FormControl>
                </HStack>
              </HStack>
            </Box>
            <Box
              mt={2}
              h={{ base: '55vh', md: 'calc(100vh - 320px)' }}
              minH="360px"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              position="relative"
            >
              <DataTable
                key={activeTab}
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                filterOptions={{
                  client_id: clientOptions,
                  service_type: requestTypeOptions,
                }}
                customRenderers={customRenderers}
                onDataLoad={handleDataLoad}
                actionLoadingStates={{
                  ...(acknowledgingId !== null && { [acknowledgingId]: true }),
                  ...(assigningId !== null && { [assigningId]: true }),
                }}
              />
              {isTabSwitching ? (
                <VStack
                  position="absolute"
                  inset={0}
                  justify="center"
                  align="center"
                  spacing={3}
                  bg="whiteAlpha.800"
                  _dark={{ bg: 'blackAlpha.500' }}
                  zIndex={2}
                >
                  <Spinner size="md" />
                  <Text fontSize="sm" color="gray.500">Loading requests...</Text>
                </VStack>
              ) : null}
            </Box>
          </CardBody>
        </Card>
      </VStack>

      <Modal
        isOpen={isTrashConfirmOpen}
        onClose={isTrashConfirmProcessing ? () => {} : onTrashConfirmClose}
        isCentered
        closeOnOverlayClick={!isTrashConfirmProcessing}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>
            {pendingTrashAction ? trashConfirmMessage(pendingTrashAction) : ''}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTrashConfirmClose} isDisabled={isTrashConfirmProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme={pendingTrashAction?.kind === 'force' ? 'red' : 'blue'}
              onClick={handleConfirmTrashAction}
              isLoading={isTrashConfirmProcessing}
            >
              {pendingTrashAction?.kind === 'force' ? 'Delete Permanently' : 'Confirm'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isAckConfirmOpen}
        onClose={isAckConfirmProcessing ? () => {} : onAckConfirmClose}
        isCentered
        closeOnOverlayClick={!isAckConfirmProcessing}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save & Acknowledge</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                Acknowledge this request and stop the incoming timer. Open the request in the drawer first if you need
                to edit fields before acknowledging.
              </Text>
              <Checkbox
                colorScheme="blue"
                isChecked={ackCreateTicket}
                onChange={(e) => setAckCreateTicket(e.target.checked)}
              >
                Also create parent ticket
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAckConfirmClose} isDisabled={isAckConfirmProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => void handleConfirmAcknowledge()}
              isLoading={isAckConfirmProcessing}
            >
              Save & Acknowledge
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ComposeEmailDrawer
        isOpen={composeRow != null}
        onClose={() => setComposeRow(null)}
        row={composeRow}
      />

      <TicketRequestTriageDrawer
        isOpen={viewRow != null}
        onClose={() => setViewRow(null)}
        row={viewRow}
        readOnly={activeTab === 'trash'}
        onComposeEmail={
          activeTab === 'trash'
            ? undefined
            : (row) => {
                setComposeRow(row)
              }
        }
        onCallOut={
          activeTab === 'trash' || !ALICE_CALL_OUT_ENABLED ? undefined : handleOpenCallOut
        }
        onAcknowledged={
          activeTab === 'trash'
            ? undefined
            : (payload) => {
                setViewRow(null)
                finishAcknowledge(payload.createTicket, payload.parentTicketId)
              }
        }
        onCreateTicket={
          activeTab === 'trash'
            ? undefined
            : (row) => {
                setViewRow(null)
                handleCreateTicket(row)
              }
        }
        showDelete={access.can_delete}
        deleteLabel={activeTab === 'trash' ? 'Delete Permanently' : 'Delete'}
        onDelete={() => {
          const id = viewRow?.id as number | undefined
          if (!id) return
          openTrashConfirm({ kind: activeTab === 'trash' ? 'force' : 'soft', id })
        }}
        isDeleting={isTrashConfirmProcessing}
      />

      {ALICE_CALL_OUT_ENABLED && callOutPayload ? (
        <AliceCallOutConfirmModal
          isOpen={isCallOutOpen}
          onClose={() => {
            onCallOutClose()
            setCallOutPayload(null)
          }}
          payload={callOutPayload}
          onSubmitted={() => {
            tableRef.current?.reload()
          }}
        />
      ) : null}
    </Box>
  )
}
